import ArticleLayout from '../../components/ArticleLayout';

export default function SparPaaVarmen() {
  return (
    <ArticleLayout
      title="Sådan sparer du på varmen: Indstilling, møbler og gode vaner"
      metaTitle="Spar på varmen: 6 dokumenterede råd til lavere varmeregning"
      metaDescription="Få mest mulig varme for pengene. Se HOFOR og Energistyrelsens officielle spareråd: termostatindstilling, 20 cm frirum, udluftning og natsænkning."
      canonicalPath="/spar-paa-varmen"
      introLead="Opvarmning udgør den absolut største post på boligens energiregning, men med få fornuftige vaner kan forbruget reduceres mærkbart uden at gå på kompromis med komforten."
      introAnswer="Ifølge HOFOR og Energistyrelsen sparer du 5 % på varmeregningen for hver eneste grad, du sænker den gennemsnitlige rumtemperatur i din bolig. Samtidig opnås store besparelser ved at give radiatorerne 20 cm frit rum og indstille alle termostater i samme opholdsrum fuldstændig ens."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: De 6 mest effektive spareråd' },
        { id: 'fem-procent', title: '2. 1 grad sænkning = 5 % besparelse' },
        { id: 'moebler-gardiner', title: '3. Hold 20 cm afstand med møbler og gardiner' },
        { id: 'udluftning-regler', title: '4. Udluftning: 4–5 minutter med gennemtræk' },
        { id: 'natsaenkning', title: '5. Myten om kraftig natsænkning' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: De 6 officielle råd til lavere varmeregning"
      checklist={[
        {
          title: 'Sænk temperaturen til 20–21 °C i opholdsrum og hold min. 18 °C',
          description: 'Hver grad du skruer ned, skærer 5 % af regningen, men gå aldrig under 18 °C for at undgå fugt og skimmelsvamp.',
        },
        {
          title: 'Stil alle termostater i samme rum på det samme trin',
          description: 'To radiatorer på trin 3 giver rolig, behagelig varme og markant bedre afkøling end én slukket og én på 5.',
        },
        {
          title: 'Hold mindst 20 cm frirum foran alle radiatorer',
          description: 'Flyt sofaer, reoler og store møbler væk, og undgå at hænge tunge gardiner eller vådt tøj hen over radiatoren.',
        },
        {
          title: 'Luft ud med kraftigt gennemtræk i 4–5 minutter ad gangen',
          description: 'Luk vinduerne helt op i få minutter 2–3 gange dagligt i stedet for at lade et vindue stå på klem i timevis.',
        },
        {
          title: 'Træk gardiner og persienner for om aftenen',
          description: 'Trukket for vinduerne om aftenen reducerer gardiner varmestrålingstab mod kolde ruder med op til 10–15 %.',
        },
        {
          title: 'Luk dørene mellem rum med forskellig temperatur',
          description: 'Hold soveværelsesdøren lukket, så den varme luft fra stuen ikke suges ind og afkøles i det kølige værelse.',
        },
      ]}
      sections={[
        {
          id: 'fem-procent',
          title: '1 grad sænkning = 5 % besparelse på varmeregningen',
          content: (
            <>
              <p>
                Det mest effektive enkeltråd til en lavere varmeregning er at holde en stabil, moderat temperatur.
              </p>
              <p>
                Forsyningsselskabet HOFOR fastslår, at for hver grad rumtemperaturen sænkes, reduceres varmetabet gennem vægge og vinduer tilsvarende, hvilket giver en ren besparelse på ca. <span className="font-mono font-bold">5 %</span> på årsforbruget.
              </p>
              <p>
                Hvis en familie sænker stuetemperaturen fra 23 °C til 20 °C, svarer det til en direkte besparelse på ca. 15 % på varmeregningen – ofte 1.500–3.000 kr. årligt for et almindeligt parcelhus.
              </p>
            </>
          ),
        },
        {
          id: 'moebler-gardiner',
          title: 'Giv radiatoren luft: 20 cm frirum er afgørende',
          content: (
            <>
              <p>
                En radiator skal kunne trække kold luft ind i bunden og aflevere opvarmet luft uhindret fra toppen for at opvarme rummet:
              </p>
              <p>
                Hvis en stor stofsofa eller en bred reol placeres klos op ad radiatoren, bliver varmen spærret inde bag møblet. Termostaten måler den indelukkede hede og lukker hurtigt ned for vandet – med det resultat, at resten af stuen forbliver kold, selvom termostaten står på 4 eller 5.
              </p>
              <p>
                Hold derfor minimum <span className="font-mono">20 cm</span> luft mellem radiatoren og alle former for møblement.
              </p>
            </>
          ),
        },
        {
          id: 'udluftning-regler',
          title: 'Udluftning: 4–5 minutter med gennemtræk',
          content: (
            <>
              <p>
                Frisk luft er nødvendig for at fjerne fugt fra madlavning, bad og vejrtrækning, men udluftningsmetoden har enorm betydning for energiforbruget:
              </p>
              <p>
                <strong>Vinduer på klem er energispild:</strong> Lader man et vindue stå på klem i timevis, afkøles vægge, gulve og møbler omkring vinduet. Når vinduet endelig lukkes, kræver det enorme mængder energi at genopvarme de kolde bygningsdele.
              </p>
              <p>
                <strong>Chokudluftning i 4–5 minutter:</strong> Åbn vinduer i begge ender af boligen på vid gab i blot 4–5 minutter. Den fugtige luft udskiftes lynhurtigt med frisk, tør luft, mens vægge og møbler bevarer deres varme. Når vinduerne lukkes, er rummet behageligt varmt igen efter få øjeblikke.
              </p>
            </>
          ),
        },
        {
          id: 'natsaenkning',
          title: 'Myten om kraftig natsænkning: Hvorfor det kan koste penge',
          content: (
            <>
              <p>
                Mange skruer radiatorerne ned på 1 hver aften og op på 4 hver morgen. Det er sjældent en god idé i velisolerede huse:
              </p>
              <p>
                Et voldsomt temperaturfald om natten betyder, at husets tunge murværk og betongulve køles ned. Næste morgen skal varmeanlægget arbejde på absolut overtryk for at indhente temperaturen. På fjernvarme fører det ofte til ringe afkøling og høje returtemperaturer.
              </p>
              <p>
                En moderat natsænkning på højst <span className="font-mono">2–3 °C</span> er hensigtsmæssig, men lad aldrig huset køle mere ned end det, da genopvarmningen ellers bruger den sparede energi.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår betaler det sig at tilkalde en energirådgiver eller VVS’er?',
        content: (
          <p>
            Hvis varmeregningen er uforklarligt høj trods gode vaner, skyldes problemet typisk manglende rør-isolering i uopvarmede kældre (hvor uisolerede rør let koster 1.000–2.000 kr. årligt i spild ifølge HOFOR), forkert varmekurve på fyret eller defekte radiatorventiler uden hydraulisk forindstilling.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Kan man tørre vasketøj på radiatoren?',
          a: 'Nej. Våde håndklæder eller tøj på radiatoren blokerer for varmeafgivelsen og fordamper store mængder fugt direkte ud i rummet, hvilket øger luftfugtigheden og risikoen for skimmelsvamp markant.',
        },
        {
          q: 'Hvad er den mest økonomiske temperatur i stuen?',
          a: 'Energistyrelsen anbefaler 20–21 °C (svarende til trin 3 på Danfoss-termostaten) i opholdsrum som stue og alrum.',
        },
        {
          q: 'Skal man lukke for termostaten, mens man lufter ud?',
          a: 'Ja. Hvis du lufter ud med kold udeluft direkte mod termostaten i mere end et par minutter, registrerer termostaten kulden og åbner for fuld varme for at modvirke trækket. Skru den ned under udluftningen.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-termostat-indstilling', text: 'Hvad betyder tallene på termostaten i grader?', desc: 'find det rette trin' },
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden', desc: 'sådan sikrer du korrekt afkøling' },
        { to: '/fjernvarme-radiator-virker-ikke', text: 'Fjernvarme og radiatorer', desc: 'undgå strafgebyr på returtemperaturen' },
        { to: '/hvordan-virker-en-radiator', text: 'Hvordan virker en radiator?', desc: 'lær princippet bag konvektion og strålevarme' },
      ]}
      sources={[
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/spar-paa-varme-og-vand-i-hjemmet', label: 'Normalt varmeforbrug i kWh (+ fjernvarme i MWh) | SparEnergi' },
        { url: 'https://www.bolius.dk/spar-paa-varmen', label: 'Spar på varmen | Få gode råd til en mindre varmeregning' },
        { url: 'https://www.danfoss.com/da-dk/industries/buildings-residential/dhs/solutions-for-homeowners/saving-energy/', label: 'Gode råd til at spare på varmen | Danfoss' },
      ]}
    />
  );
}
